import { adminFetch } from '../../lib/adminFetch';
import React, { useState, useEffect } from 'react';

const VideoAdmin = () => {
  const [videos, setVideos] = useState([]);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', url: '', description: '', category: '', access: 'all' });
  const [editIndex, setEditIndex] = useState(null);

  useEffect(() => {
    adminFetch('/api/admin-read.php?file=academy-videos&t=' + Date.now())
      .then((r) => r.ok ? r.json() : [])
      .then(setVideos)
      .catch(() => setVideos([]));
  }, []);

  const saveVideos = async (data) => {
    setSaving(true);
    try {
      const res = await adminFetch('/api/admin-data.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file: 'academy-videos', data }),
      });
      const result = await res.json();
      if (result.success) {
        setVideos(data);
        setMessage('保存しました');
        setTimeout(() => setMessage(''), 2000);
      }
    } catch {}
    setSaving(false);
  };

  const addVideo = () => {
    if (!form.title.trim() || !form.url.trim()) return;
    const video = { ...form, id: String(Date.now()), createdAt: new Date().toISOString() };
    saveVideos([video, ...videos]);
    setForm({ title: '', url: '', description: '', category: '', access: 'all' });
    setShowForm(false);
  };

  const updateVideo = () => {
    if (editIndex === null) return;
    const updated = [...videos];
    updated[editIndex] = { ...updated[editIndex], ...form };
    saveVideos(updated);
    setEditIndex(null);
    setForm({ title: '', url: '', description: '', category: '', access: 'all' });
    setShowForm(false);
  };

  const deleteVideo = (index) => {
    if (!window.confirm('この動画を削除しますか？')) return;
    saveVideos(videos.filter((_, i) => i !== index));
  };

  const startEdit = (index) => {
    const v = videos[index];
    setForm({ title: v.title, url: v.url, description: v.description || '', category: v.category || '', access: v.access || 'all' });
    setEditIndex(index);
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditIndex(null);
    setForm({ title: '', url: '', description: '', category: '', access: 'all' });
  };

  // アカデミー契約済みの顧客にメール通知
  const [notifying, setNotifying] = useState(false);
  const notifyMembers = async (video) => {
    if (!window.confirm(`「${video.title}」の公開をアカデミー契約済みの顧客に通知しますか？`)) return;
    setNotifying(true);
    try {
      // 契約済み顧客のメール取得
      const custRes = await adminFetch('/api/admin-read.php?file=customers&t=' + Date.now());
      const customers = await custRes.json();
      const emails = customers.filter((c) => c.email && (c.srwithContract === 'あり' || c.academyStatus === '受講中')).map((c) => c.email);

      if (emails.length === 0) {
        setMessage('アカデミー契約済みの顧客がいません');
        setNotifying(false);
        return;
      }

      const formData = new FormData();
      formData.append('subject', `【SrWithアカデミー】新しい動画が公開されました: ${video.title}`);
      formData.append('body', `新しい動画コンテンツが公開されました。\n\n`
        + `タイトル: ${video.title}\n`
        + (video.description ? `内容: ${video.description}\n` : '')
        + `\n以下のURLからご覧いただけます。\nhttps://srwith.com/academy/members\n`);
      formData.append('emails', JSON.stringify(emails));

      const res = await adminFetch('/api/admin-ml.php?action=send', { method: 'POST', body: formData });
      const data = await res.json();
      if (data.success) {
        setMessage(`${data.sentCount}件に通知しました`);
      } else {
        setMessage('通知に失敗しました');
      }
    } catch { setMessage('通知に失敗しました'); }
    setNotifying(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('ja-JP');
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">動画管理</h2>
        <button onClick={() => { setShowForm(true); setEditIndex(null); setForm({ title: '', url: '', description: '', category: '', access: 'all' }); }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-blue-700">+ 新規動画</button>
      </div>

      {message && <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded-lg text-sm">{message}</div>}

      {showForm && (
        <div className="bg-white rounded-xl shadow p-6 mb-4 space-y-3">
          <h3 className="font-bold">{editIndex !== null ? '動画を編集' : '新規動画を追加'}</h3>
          <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="タイトル" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <input type="text" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
            placeholder="動画URL（YouTube限定公開等）" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <div className="grid grid-cols-2 gap-3">
            <input type="text" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              placeholder="カテゴリ（例: 勉強会, LAB, その他）" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <select value={form.access} onChange={(e) => setForm({ ...form, access: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="all">全員公開</option>
              <option value="contract">契約者限定（SrWith + アカデミー）</option>
              <option value="academy">アカデミー限定</option>
            </select>
          </div>
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3}
            placeholder="説明（任意）" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
          <div className="flex gap-3">
            <button onClick={editIndex !== null ? updateVideo : addVideo} disabled={saving || !form.title.trim() || !form.url.trim()}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50">
              {editIndex !== null ? '更新' : '追加'}
            </button>
            <button onClick={cancelForm} className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-bold hover:bg-gray-300">キャンセル</button>
          </div>
        </div>
      )}

      {videos.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">動画がありません</div>
      ) : (
        <div className="space-y-3">
          {videos.map((v, i) => (
            <div key={v.id || i} className="bg-white rounded-xl shadow p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-sm">{v.title}</h3>
                    {v.category && <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-purple-100 text-purple-700">{v.category}</span>}
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${{ all: 'bg-gray-100 text-gray-600', contract: 'bg-green-100 text-green-700', academy: 'bg-emerald-100 text-emerald-700' }[v.access || 'all']}`}>
                      {{ all: '全員公開', contract: '契約者限定', academy: 'アカデミー限定' }[v.access || 'all']}
                    </span>
                  </div>
                  <a href={v.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline break-all">{v.url}</a>
                  {v.description && <p className="text-xs text-gray-500 mt-1">{v.description}</p>}
                  <p className="text-[10px] text-gray-400 mt-1">{formatDate(v.createdAt)}</p>
                </div>
                <div className="flex gap-1.5 shrink-0 ml-3">
                  <button onClick={() => notifyMembers(v)} disabled={notifying}
                    className="bg-green-100 text-green-700 px-3 py-1 rounded text-[10px] font-bold hover:bg-green-200 disabled:opacity-50">通知</button>
                  <button onClick={() => startEdit(i)}
                    className="bg-gray-100 text-gray-600 px-3 py-1 rounded text-[10px] font-bold hover:bg-gray-200">編集</button>
                  <button onClick={() => deleteVideo(i)}
                    className="bg-red-50 text-red-600 px-3 py-1 rounded text-[10px] font-bold hover:bg-red-100">削除</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VideoAdmin;
